import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { Controller, FieldValues, Path, PathValue } from 'react-hook-form'
import { InputProps } from '../../../store/types'
import dayjs, { Dayjs } from 'dayjs'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import DateFormat from '../../../store/types/enums/DateFormat'
import i18n from '../../../config/i18n'
import { SxProps } from '@mui/material'

interface DateInputProps<T extends FieldValues> extends InputProps<T> {
  defaultValue?: PathValue<T, Path<T>>
  minDate?: Dayjs
  dateFormat?: string
  flex1?: boolean
  sxProps?: SxProps
  onValueChange?: (date: Dayjs | null) => void
  hideHelperText?: boolean
}

const DateInput = <T extends FieldValues>({
  name,
  control,
  label,
  rules,
  defaultValue,
  minDate,
  dateFormat,
  flex1,
  sxProps,
  onValueChange,
  hideHelperText,
}: DateInputProps<T>) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={i18n.language}>
      <Controller
        name={name}
        control={control}
        rules={rules}
        defaultValue={defaultValue}
        render={({ field: { value, onChange, onBlur, name }, fieldState }) => (
          <DatePicker
            value={value || null}
            onChange={(date: Dayjs | null) => {
              const utcDate = date ? dayjs(date).utc(true) : null
              onChange(utcDate)
              onValueChange?.(utcDate)
            }}
            label={label}
            minDate={minDate || dayjs('1900-01-01').utc(true)}
            format={dateFormat || DateFormat.DayMonthYear}
            slotProps={{
              textField: {
                size: 'small',
                helperText: hideHelperText ? '' : fieldState.error?.message,
                error: !!fieldState.error,
                name: name,
                onBlur: onBlur,
              },
            }}
            sx={{
              svg: {
                color: 'text.mediumEmphasis',
              },
              ...(flex1 && { flex: 1 }),
              ...sxProps,
            }}
          />
        )}
      />
    </LocalizationProvider>
  )
}

export default DateInput
